import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Tag, Pagination, Skeleton, Empty, Select, Input } from 'antd';
import { CalendarOutlined, EyeOutlined, LikeOutlined, MessageOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { mockPosts, mockCategories, mockTags } from '../../api/mockData';
import PostCard from '../../components/PostCard';
import './index.css';

const { Search } = Input;
const { Option } = Select;

const Home = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(9);
  const [total, setTotal] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTag, setSelectedTag] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    // 模拟数据加载
    setTimeout(() => {
      const filteredPosts = filterPosts(mockPosts);
      setPosts(filteredPosts);
      setTotal(filteredPosts.length);
      setLoading(false);
    }, 500);
  }, [selectedCategory, selectedTag, searchQuery]);

  const filterPosts = (allPosts) => {
    let filtered = [...allPosts];
    
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(post => post.category === selectedCategory);
    }
    
    if (selectedTag !== 'all') {
      filtered = filtered.filter(post => post.tags.includes(selectedTag));
    }
    
    if (searchQuery) {
      filtered = filtered.filter(post => 
        post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }
    
    return filtered;
  };

  const handlePageChange = (page, size) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const handleSearch = (value) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handleCategoryChange = (value) => {
    setSelectedCategory(value);
    setCurrentPage(1);
  };

  const handleTagChange = (value) => {
    setSelectedTag(value);
    setCurrentPage(1);
  };

  const getCurrentPagePosts = () => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return posts.slice(startIndex, endIndex);
  };

  const popularPosts = mockPosts
    .sort((a, b) => b.views - a.views)
    .slice(0, 5);

  const recentPosts = [...mockPosts]
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, 5);

  return (
    <div className="home-page">
      {/* 顶部横幅 */}
      <div className="hero-banner">
        <div className="hero-content">
          <h1>欢迎来到技术博客</h1>
          <p>分享技术、记录成长、探索无限可能</p>
          <Search
            placeholder="搜索文章..."
            size="large"
            className="hero-search"
            onSearch={handleSearch}
            enterButton
          />
        </div>
      </div>

      <div className="container">
        <Row gutter={[24, 24]}>
          {/* 左侧内容区域 */}
          <Col xs={24} lg={18}>
            {/* 筛选器 */}
            <Card className="filter-card" size="small">
              <Row gutter={[16, 16]} align="middle">
                <Col xs={24} sm={8}>
                  <Select
                    value={selectedCategory}
                    onChange={handleCategoryChange}
                    className="filter-select"
                    placeholder="选择分类"
                    style={{ width: '100%' }}
                  >
                    <Option value="all">所有分类</Option>
                    {mockCategories.map(category => (
                      <Option key={category} value={category}>{category}</Option>
                    ))}
                  </Select>
                </Col>
                <Col xs={24} sm={8}>
                  <Select
                    value={selectedTag}
                    onChange={handleTagChange}
                    className="filter-select"
                    placeholder="选择标签"
                    style={{ width: '100%' }}
                  >
                    <Option value="all">所有标签</Option>
                    {mockTags.map(tag => (
                      <Option key={tag} value={tag}>{tag}</Option>
                    ))}
                  </Select>
                </Col>
                <Col xs={24} sm={8}>
                  <div className="post-count">
                    共找到 {total} 篇文章
                  </div>
                </Col>
              </Row>
            </Card>

            {/* 文章列表 */}
            {loading ? (
              <div className="posts-grid">
                {[1, 2, 3, 4, 5, 6].map(i => (
                  <Col xs={24} sm={12} lg={8} key={i}>
                    <Card className="blog-card">
                      <Skeleton active paragraph={{ rows: 3 }} />
                    </Card>
                  </Col>
                ))}
              </div>
            ) : posts.length > 0 ? (
              <>
                <Row gutter={[24, 24]} className="posts-grid">
                  {getCurrentPagePosts().map(post => (
                    <Col xs={24} sm={12} lg={8} key={post.id}>
                      <PostCard post={post} />
                    </Col>
                  ))}
                </Row>
                
                {/* 分页 */}
                <div className="pagination-container">
                  <Pagination
                    current={currentPage}
                    pageSize={pageSize}
                    total={total}
                    onChange={handlePageChange}
                    showSizeChanger
                    showQuickJumper
                    showTotal={(total) => `共 ${total} 条`}
                  />
                </div>
              </>
            ) : (
              <Empty description="暂无文章" />
            )}
          </Col>

          {/* 右侧边栏 */}
          <Col xs={24} lg={6}>
            {/* 热门文章 */}
            <Card title="热门文章" className="sidebar-card">
              {popularPosts.map((post, index) => (
                <Link to={`/post/${post.id}`} key={post.id}>
                  <div className="sidebar-post-item">
                    <div className="post-rank">{index + 1}</div>
                    <div className="post-content">
                      <h4>{post.title}</h4>
                      <div className="post-meta">
                        <EyeOutlined /> {post.views}
                        <LikeOutlined /> {post.likes}
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </Card>

            {/* 最新文章 */}
            <Card title="最新文章" className="sidebar-card">
              {recentPosts.map(post => (
                <Link to={`/post/${post.id}`} key={post.id}>
                  <div className="sidebar-post-item">
                    <div className="post-content">
                      <h4>{post.title}</h4>
                      <div className="post-meta">
                        <CalendarOutlined /> {post.date}
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </Card>

            {/* 标签云 */}
            <Card title="标签云" className="sidebar-card">
              <div className="tag-cloud">
                {mockTags.slice(0, 15).map(tag => (
                  <Tag
                    key={tag}
                    color={tag === selectedTag ? 'blue' : undefined}
                    className="tag-cloud-item"
                    onClick={() => handleTagChange(tag)}
                  >
                    {tag}
                  </Tag>
                ))}
              </div>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default Home;